import React from "react";
import { Layout } from "src/components/custom/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Badge } from "src/components/ui/Badge";
import { FileText, Shield } from "lucide-react";

export default function TnC() {
  return (
    <Layout>
      <Layout.Body>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        {/* Header */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700 border-b border-slate-200/50 dark:border-slate-700/50">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/5 dark:from-blue-400/5 dark:to-indigo-300/5" />
          <div className="relative px-6 py-12 md:px-8 md:py-16">
            <div className="mx-auto max-w-4xl text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <h1 className="mb-4 text-4xl font-bold tracking-tight text-slate-900 dark:text-white md:text-5xl">
                Terms and Conditions
              </h1>
              <p className="mx-auto max-w-2xl text-lg text-slate-600 dark:text-slate-300 mb-6">
                Please read these terms and conditions carefully before using Our Service.
              </p>
              <Badge variant="outline" className="text-slate-600 border-slate-300 bg-slate-50/50 dark:text-slate-400 dark:border-slate-600 dark:bg-slate-800/30">
                Last updated: August 21, 2024
              </Badge>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-12 md:px-8">
          <div className="mx-auto max-w-4xl space-y-8">

            {/* Interpretation and Definitions */}
            <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl text-slate-900 dark:text-white">
                  <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  Interpretation and Definitions
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate dark:prose-invert max-w-none">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Interpretation</h3>
                <p className="text-slate-700 dark:text-slate-300">
                  The words of which the initial letter is capitalized have meanings defined under the following conditions. 
                  The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
                </p>
                
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">Definitions</h3>
                <p className="text-slate-700 dark:text-slate-300">For the purposes of these Terms and Conditions:</p>
                
                <div className="space-y-4 mt-4">
                  <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                    <strong className="text-slate-900 dark:text-white">Application</strong> means the software program provided by the Company downloaded by You on any electronic device, named Dharam Kaata
                  </div>
                  <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                    <strong className="text-slate-900 dark:text-white">Application Store</strong> means the digital distribution service operated and developed by Apple Inc. (Apple App Store) or Google Inc. (Google Play Store) in which the Application has been downloaded.
                  </div>
                  <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                    <strong className="text-slate-900 dark:text-white">Affiliate</strong> means an entity that controls, is controlled by or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.
                  </div>
                  <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                    <strong className="text-slate-900 dark:text-white">Country</strong> refers to: Jharkhand, India
                  </div>
                  <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                    <strong className="text-slate-900 dark:text-white">Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to Dharam Kaata, Chatra, Jharkhand.
                  </div>
                  <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                    <strong className="text-slate-900 dark:text-white">Device</strong> means any device that can access the Service such as a computer, a cellphone or a digital tablet.
                  </div>
                  <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                    <strong className="text-slate-900 dark:text-white">Service</strong> refers to the Application or the Website or both.
                  </div>
                  <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                    <strong className="text-slate-900 dark:text-white">Terms and Conditions</strong> (also referred as "Terms") mean these Terms and Conditions that form the entire agreement between You and the Company regarding the use of the Service. This Terms and Conditions agreement has been created with the help of the{" "}
                    <a
                      href="https://www.termsfeed.com/terms-conditions-generator/"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Terms and Conditions Generator
                    </a>.
                  </div>
                  <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                    <strong className="text-slate-900 dark:text-white">Third-party Social Media Service</strong> means any services or content (including data, information, products or services) provided by a third-party that may be displayed, included or made available by the Service.
                  </div>
                  <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                    <strong className="text-slate-900 dark:text-white">Website</strong> refers to Dharam Kaata, accessible from{" "}
                    <a
                      href="https://www.dharamkaata.in"
                      rel="external nofollow noopener noreferrer"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      https://www.dharamkaata.in
                    </a>
                  </div>
                  <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                    <strong className="text-slate-900 dark:text-white">You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Acknowledgment */}
            <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-900 dark:text-white">Acknowledgment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-700 dark:text-slate-300">
                <p>
                  These are the Terms and Conditions governing the use of this Service and the agreement that operates between You and the Company. These Terms and Conditions set out the rights and obligations of all users regarding the use of the Service.
                </p>
                <p>
                  Your access to and use of the Service is conditioned on Your acceptance of and compliance with these Terms and Conditions. These Terms and Conditions apply to all visitors, users and others who access or use the Service.
                </p>
                <p>
                  By accessing or using the Service You agree to be bound by these Terms and Conditions. If You disagree with any part of these Terms and Conditions then You may not access the Service.
                </p>
                <p>
                  You represent that you are over the age of 18. The Company does not permit those under 18 to use the Service.
                </p>
                <p>
                  Your access to and use of the Service is also conditioned on Your acceptance of and compliance with the Privacy Policy of the Company. Our Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your personal information when You use the Application or the Website and tells You about Your privacy rights and how the law protects You. Please read Our Privacy Policy carefully before using Our Service.
                </p>
              </CardContent>
            </Card>

            {/* Two Column Layout for Key Sections */}
            <div className="grid gap-8 md:grid-cols-2">
              
              {/* Links to Other Websites */}
              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Links to Other Websites</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-slate-700 dark:text-slate-300 text-sm">
                  <p>Our Service may contain links to third-party web sites or services that are not owned or controlled by the Company.</p>
                  <p>The Company has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third party web sites or services. You further acknowledge and agree that the Company shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods or services available on or through any such web sites or services.</p>
                  <p>We strongly advise You to read the terms and conditions and privacy policies of any third-party web sites or services that You visit.</p>
                </CardContent>
              </Card>

              {/* Termination */}
              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Termination</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-slate-700 dark:text-slate-300 text-sm">
                  <p>We may terminate or suspend Your access immediately, without prior notice or liability, for any reason whatsoever, including without limitation if You breach these Terms and Conditions.</p>
                  <p>Upon termination, Your right to use the Service will cease immediately.</p>
                </CardContent>
              </Card>
            </div>

            {/* Limitation of Liability */}
            <Card className="border-amber-200/60 bg-amber-50/30 dark:bg-amber-950/20 dark:border-amber-800/30">
              <CardHeader>
                <CardTitle className="text-2xl text-amber-900 dark:text-amber-100">Limitation of Liability</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-amber-800 dark:text-amber-200 text-sm">
                <p>
                  Notwithstanding any damages that You might incur, the entire liability of the Company and any of its suppliers under any provision of this Terms and Your exclusive remedy for all of the foregoing shall be limited to the amount actually paid by You through the Service or 100 USD if You haven't purchased anything through the Service.
                </p>
                <p>
                  To the maximum extent permitted by applicable law, in no event shall the Company or its suppliers be liable for any special, incidental, indirect, or consequential damages whatsoever (including, but not limited to, damages for loss of profits, loss of data or other information, for business interruption, for personal injury, loss of privacy arising out of or in any way related to the use of or inability to use the Service, third-party software and/or third-party hardware used with the Service, or otherwise in connection with any provision of this Terms), even if the Company or any supplier has been advised of the possibility of such damages and even if the remedy fails of its essential purpose.
                </p>
                <p>
                  Some states do not allow the exclusion of implied warranties or limitation of liability for incidental or consequential damages, which means that some of the above limitations may not apply. In these states, each party's liability will be limited to the greatest extent permitted by law.
                </p>
              </CardContent>
            </Card>

            {/* "AS IS" and "AS AVAILABLE" Disclaimer */}
            <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-900 dark:text-white">"AS IS" and "AS AVAILABLE" Disclaimer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-700 dark:text-slate-300 text-sm">
                <p>
                  The Service is provided to You "AS IS" and "AS AVAILABLE" and with all faults and defects without warranty of any kind. To the maximum extent permitted under applicable law, the Company, on its own behalf and on behalf of its Affiliates and its and their respective licensors and service providers, expressly disclaims all warranties, whether express, implied, statutory or otherwise, with respect to the Service, including all implied warranties of merchantability, fitness for a particular purpose, title and non-infringement, and warranties that may arise out of course of dealing, course of performance, usage or trade practice. Without limitation to the foregoing, the Company provides no warranty or undertaking, and makes no representation of any kind that the Service will meet Your requirements, achieve any intended results, be compatible or work with any other software, applications, systems or services, operate without interruption, meet any performance or reliability standards or be error free or that any errors or defects can or will be corrected.
                </p>
                <p>
                  Without limiting the foregoing, neither the Company nor any of the company's provider makes any representation or warranty of any kind, express or implied: (i) as to the operation or availability of the Service, or the information, content, and materials or products included thereon; (ii) that the Service will be uninterrupted or error-free; (iii) as to the accuracy, reliability, or currency of any information or content provided through the Service; or (iv) that the Service, its servers, the content, or e-mails sent from or on behalf of the Company are free of viruses, scripts, trojan horses, worms, malware, timebombs or other harmful components.
                </p>
                <p>
                  Some jurisdictions do not allow the exclusion of certain types of warranties or limitations on applicable statutory rights of a consumer, so some or all of the above exclusions and limitations may not apply to You. But in such a case the exclusions and limitations set forth in this section shall be applied to the greatest extent enforceable under applicable law.
                </p>
              </CardContent>
            </Card>

            {/* Legal Sections in Grid */}
            <div className="grid gap-8 md:grid-cols-2">
              
              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Governing Law</CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700 dark:text-slate-300 text-sm">
                  <p>The laws of the Country, excluding its conflicts of law rules, shall govern this Terms and Your use of the Service. Your use of the Application may also be subject to other local, state, national, or international laws.</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Disputes Resolution</CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700 dark:text-slate-300 text-sm">
                  <p>If You have any concern or dispute about the Service, You agree to first try to resolve the dispute informally by contacting the Company.</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">For European Union (EU) Users</CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700 dark:text-slate-300 text-sm">
                  <p>If You are a European Union consumer, you will benefit from any mandatory provisions of the law of the country in which You are resident.</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">United States Legal Compliance</CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700 dark:text-slate-300 text-sm">
                  <p>You represent and warrant that (i) You are not located in a country that is subject to the United States government embargo, or that has been designated by the United States government as a "terrorist supporting" country, and (ii) You are not listed on any United States government list of prohibited or restricted parties.</p>
                </CardContent>
              </Card>
            </div>

            {/* Severability and Waiver */}
            <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-900 dark:text-white">Severability and Waiver</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 text-slate-700 dark:text-slate-300">
                <div>
                  <h4 className="font-semibold text-lg text-slate-900 dark:text-white mb-2">Severability</h4>
                  <p className="text-sm">
                    If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-lg text-slate-900 dark:text-white mb-2">Waiver</h4>
                  <p className="text-sm">
                    Except as provided herein, the failure to exercise a right or to require performance of an obligation under these Terms shall not affect a party's ability to exercise such right or require such performance at any time thereafter nor shall the waiver of a breach constitute a waiver of any subsequent breach.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Final Sections in Grid */}
            <div className="grid gap-8 md:grid-cols-2">
              
              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Translation Interpretation</CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700 dark:text-slate-300 text-sm">
                  <p>These Terms and Conditions may have been translated if We have made them available to You on our Service. You agree that the original English text shall prevail in the case of a dispute.</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Changes to These Terms and Conditions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-slate-700 dark:text-slate-300 text-sm">
                  <p>We reserve the right, at Our sole discretion, to modify or replace these Terms at any time. If a revision is material We will make reasonable efforts to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at Our sole discretion.</p>
                  <p>By continuing to access or use Our Service after those revisions become effective, You agree to be bound by the revised terms. If You do not agree to the new terms, in whole or in part, please stop using the website and the Service.</p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Us */}
            <Card className="border-blue-200/60 bg-blue-50/30 dark:bg-blue-950/20 dark:border-blue-800/30">
              <CardHeader>
                <CardTitle className="text-2xl text-blue-900 dark:text-blue-100">Contact Us</CardTitle>
              </CardHeader>
              <CardContent className="text-blue-800 dark:text-blue-200">
                <p className="mb-4">If you have any questions about these Terms and Conditions, You can contact us:</p>
                <div className="rounded-lg bg-blue-100/50 dark:bg-blue-900/30 p-4">
                  <p className="font-medium">By email: yuvraj@dharamkaata.com</p>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200/50 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-900/50 px-6 py-8 md:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Generated using{" "}
              <a
                href="https://www.termsfeed.com/privacy-policy-generator/"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                TermsFeed Privacy Policy Generator
              </a>
            </p>
          </div>
        </div>
      </div>
      </Layout.Body>
    </Layout>
  );
}